import { useCallback, useState } from 'react'
import { useAnalyticsFeatureFlag } from '../contexts/AnalyticsFeatureFlagContext'
import { QuotaResults } from '../data/model/quotaResults'
import { buildRequestUrl } from '../utils/apiUtils'
import { AccessToken } from '../utils/authUtils'
import requestWithRetry from '../utils/requestWithRetry'

const LEGACY_QUOTA_RESULTS_ENDPOINT = `${
  process.env.REACT_APP_URL_SURVEY_RESULTS || '/v2/survey-results'
}/api/v1.0/quotas`

const ANALYTICS_QUOTA_ENDPOINT = `${
  process.env.REACT_APP_FD_CORE_RESPONSES_URL || '/v1/core-responses'
}/analytics`

interface QuotaData {
  getSurveyQuotas: (surveyId: string) => void
  quotaResults: QuotaResults[] | undefined
  error: boolean
  loading: boolean
}

interface QuotaVars {
  surveyId: string
  language?: string
}

const useGetSurveyQuotas: (vars: QuotaVars) => QuotaData = ({ language }) => {
  const [quotaResults, setQuotaResults] = useState<QuotaResults[] | undefined>(
    undefined
  )
  // The language the cached quotaResults were fetched in, so a language change
  // re-requests instead of being short-circuited by the cache guard below.
  const [loadedLanguage, setLoadedLanguage] = useState<string | undefined>(
    undefined
  )
  const [error, setError] = useState<boolean>(false)
  const [loading, setLoading] = useState<boolean>(true)
  const { useAnalyticsAPI } = useAnalyticsFeatureFlag()

  const fetchLegacyQuotas = useCallback(async (surveyId: string) => {
    const legacyRequestUrl = buildRequestUrl(
      LEGACY_QUOTA_RESULTS_ENDPOINT,
      process.env.REACT_APP_REST_DOMAIN
    )

    return requestWithRetry({
      url: legacyRequestUrl,
      method: 'get',
      getConfig: () => ({
        headers: {
          ...AccessToken.requestHeaders()
        },
        params: { surveyId }
      })
    })
  }, [])

  const fetchAnalyticsQuotas = useCallback(
    async (surveyId: string) => {
      const analyticsRequestUrl = buildRequestUrl(
        `${ANALYTICS_QUOTA_ENDPOINT}/${surveyId}/quotas`,
        process.env.REACT_APP_REST_DOMAIN
      )

      return requestWithRetry({
        url: analyticsRequestUrl,
        method: 'get',
        getConfig: () => ({
          headers: {
            ...AccessToken.requestHeaders()
          },
          // Omitted when unknown so the endpoint keeps resolving question text
          // the way it does today.
          params: language ? { language } : undefined
        })
      })
    },
    [language]
  )

  const getSurveyQuotas = useCallback(
    async (surveyId: string) => {
      if (quotaResults !== undefined && loadedLanguage === language) return

      // Wait for feature flag to be determined before making any requests
      if (useAnalyticsAPI === null) {
        return
      }

      setLoading(true)
      setError(false)

      // Recorded on success only: marking the language as loaded up front would
      // make the guard above believe a failed switch had landed, blocking any
      // retry while the cards still showed the previous language's text.
      const succeed = (quotas: QuotaResults[]) => {
        setQuotaResults(quotas)
        setLoadedLanguage(language)
      }
      // Clears the results too, so a failed switch shows the error state rather
      // than the previous language's question text.
      const fail = () => {
        setQuotaResults(undefined)
        setError(true)
      }

      try {
        if (useAnalyticsAPI) {
          // Try analytics API first
          const analyticsResponse = await fetchAnalyticsQuotas(surveyId)

          const isValidResponse =
            analyticsResponse.status >= 200 && analyticsResponse.status < 400
          const hasQuotas =
            isValidResponse && analyticsResponse.data.quotas.length > 0

          if (hasQuotas) {
            succeed(analyticsResponse.data.quotas)
            return
          }

          // Fall back to legacy if analytics returns empty or invalid response
          const legacyResponse = await fetchLegacyQuotas(surveyId)
          if (legacyResponse.status >= 200 && legacyResponse.status < 400) {
            succeed(legacyResponse.data.quotas)
          } else {
            fail()
          }
        } else {
          // Feature flag disabled, use legacy API directly
          const response = await fetchLegacyQuotas(surveyId)
          if (response.status >= 200 && response.status < 400) {
            succeed(response.data.quotas)
          } else {
            fail()
          }
        }
      } catch {
        // If analytics API failed, try legacy as fallback
        if (useAnalyticsAPI) {
          try {
            const legacyResponse = await fetchLegacyQuotas(surveyId)
            if (legacyResponse.status >= 200 && legacyResponse.status < 400) {
              succeed(legacyResponse.data.quotas)
            } else {
              fail()
            }
          } catch {
            fail()
          }
        } else {
          fail()
        }
      } finally {
        setLoading(false)
      }
    },
    [
      quotaResults,
      loadedLanguage,
      language,
      fetchAnalyticsQuotas,
      fetchLegacyQuotas,
      useAnalyticsAPI
    ]
  )

  return { getSurveyQuotas, quotaResults, error, loading }
}

export default useGetSurveyQuotas
